import React from 'react';
import { Row, Col, Card, Badge, Button, Form, Alert } from 'react-bootstrap';
import {
    programApplicationDates,
    formatDate,
} from '@/components/common/programTermDates';
import {
    daysUntilClose,
    isBeforeOpen,
    isPastClose,
    releaseMoment,
} from '@/shared/utils/deadline';
import StatValue from '@/components/common/StatValue';
import useProgramAvailability from '@/shared/hooks/useProgramAvailability';
import './ProgramTermHeader.css';
import { badgeTextFor } from '@/shared/utils/badgeText';
import { termDisplayName } from '@/shared/utils/termLabel';

const PHASE_CONFIG = {
    before_applications: {
        label: 'Pre-Application',
        bg: 'secondary',
    },
    setup_incomplete: {
        label: 'Term Setup Incomplete',
        bg: 'secondary',
    },
    applications_open: {
        label: 'Applications Open',
        bg: 'success',
    },
    applications_closed: {
        label: 'Applications Closed',
        bg: 'warning',
    },
    review_period: { label: 'Review Period', bg: 'info' },
    decisions_released: {
        label: 'Decisions Released',
        bg: 'primary',
    },
    term_active: { label: 'Term In Progress', bg: 'success' },
    term_ended: { label: 'Term Complete', bg: 'dark' },
};

// Whether this program's application window is open today, by date alone.
// Kept apart from the phase: a review window that starts while applications
// are still open moves the phase on, but applications are still being taken.
function isApplicationWindowOpen(term, programType) {
    const now = new Date();
    const { open, close } = programApplicationDates(term, programType);
    // No dates anywhere is an open window, as the server judges it.
    if (!open && !close) return true;
    if (open && isBeforeOpen(open, now)) return false;
    return !(close && isPastClose(close, now));
}

function getTermPhase(term, programType) {
    if (!term) return 'before_applications';

    const now = new Date();
    const termEnd = term.endDate ? new Date(term.endDate) : null;
    const termStart = term.startDate ? new Date(term.startDate) : null;
    const { open: appOpen, close: appClose } = programApplicationDates(
        term,
        programType
    );
    const pw = term.programWindows?.[programType];
    const reviewStart = pw?.reviewStart ? new Date(pw.reviewStart) : null;
    const reviewEnd = pw?.reviewEnd ? new Date(pw.reviewEnd) : null;
    const decisionsDate = releaseMoment(pw?.decisionsReleased);

    if (termEnd && now > termEnd) return 'term_ended';
    if (decisionsDate && now >= decisionsDate) return 'decisions_released';
    // With a release date set, only its moment releases: faculty see nothing
    // between the end of review and that moment. Without one, each decision
    // is visible as it is recorded, so the end of review stands in.
    if (!decisionsDate && reviewEnd && now > reviewEnd) {
        return 'decisions_released';
    }
    if (reviewStart && now >= reviewStart) return 'review_period';
    if (appClose && isPastClose(appClose, now)) return 'applications_closed';
    if (appOpen && !isBeforeOpen(appOpen, now)) return 'applications_open';
    if (termStart && now >= termStart) {
        // Term has started but no application window is configured for this
        // program — show a neutral "setup incomplete" phase rather than
        // "Term In Progress", which implies the workflow is underway.
        if (!appOpen && !appClose) return 'setup_incomplete';
        return 'term_active';
    }
    return 'before_applications';
}

function daysUntil(dateString) {
    if (!dateString) return null;
    const diff = Math.ceil(
        (new Date(dateString) - new Date()) / (1000 * 60 * 60 * 24)
    );
    return diff;
}

/**
 * The warning that a program has not set its review window or release date.
 * It sits under the navigation, beside the work, rather than in the header.
 * A switched-off program reviews nothing through the Hub, so it has no dates
 * to set.
 */
export function MissingDatesAlert({
    programType,
    programLabel,
    selectedTerm,
    onManageDates,
}) {
    const switchedOff = useProgramAvailability()?.[programType] === false;
    const pw = selectedTerm?.programWindows?.[programType];
    if (!selectedTerm || switchedOff) return null;
    if (pw?.reviewStart && pw?.reviewEnd && pw?.decisionsReleased) return null;
    return (
        <Alert variant="warning" className="mb-3 py-2">
            <strong>{programLabel} review dates not fully configured</strong>{' '}
            for {termDisplayName(selectedTerm)}.{' '}
            {onManageDates ? (
                <Button
                    variant="link"
                    className="alert-link p-0 align-baseline text-start"
                    onClick={onManageDates}
                >
                    Set your review window and decision release date
                </Button>
            ) : (
                'Set your review window and decision release date'
            )}{' '}
            to enable timeline tracking.
        </Alert>
    );
}

function ProgramTermHeader({
    programType,
    programLabel,
    programName,
    programColor,
    terms,
    selectedTerm,
    onTermChange,
    stats,
    statsLoading = false,
    statsFailed = false,
    compact = false,
}) {
    // The program's switch is global; while it is off no window applies, so
    // the phase, the application badge and the next deadline go.
    const switchedOff = useProgramAvailability()?.[programType] === false;
    const pw = selectedTerm?.programWindows?.[programType];
    const applicationDates = programApplicationDates(selectedTerm, programType);
    const phase = getTermPhase(selectedTerm, programType);
    const phaseConfig = PHASE_CONFIG[phase];

    // Upcoming deadline calculation
    const upcomingDeadlines = [];
    if (pw?.reviewStart && daysUntil(pw.reviewStart) > 0) {
        upcomingDeadlines.push({
            label: 'Review starts',
            date: pw.reviewStart,
            days: daysUntil(pw.reviewStart),
        });
    }
    if (pw?.reviewEnd && daysUntil(pw.reviewEnd) > 0) {
        upcomingDeadlines.push({
            label: 'Review ends',
            date: pw.reviewEnd,
            days: daysUntil(pw.reviewEnd),
        });
    }
    const releaseAt = releaseMoment(pw?.decisionsReleased);
    if (releaseAt && daysUntil(releaseAt) > 0) {
        upcomingDeadlines.push({
            label: 'Decisions release',
            date: releaseAt,
            days: daysUntil(releaseAt),
        });
    }
    // Zero is the close day itself, which is still open.
    if (applicationDates.close && daysUntilClose(applicationDates.close) >= 0) {
        upcomingDeadlines.push({
            label: 'Applications close',
            date: applicationDates.close,
            days: daysUntilClose(applicationDates.close),
        });
    }
    upcomingDeadlines.sort((a, b) => a.days - b.days);

    const next = upcomingDeadlines[0];

    return (
        <>
            {/* Program Header with Term Selector */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2 mb-3">
                <div className="d-flex align-items-center">
                    <Badge
                        bg=""
                        className="me-3 pth-program-badge"
                        style={{ backgroundColor: programColor }}
                    >
                        {programLabel}
                    </Badge>
                    <div>
                        <h1 className="h3 mb-0">{programName}</h1>
                        <p className="text-muted small mb-0">
                            Coordinator Dashboard
                        </p>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <Form.Label
                        htmlFor="pth-term-selector"
                        className="visually-hidden"
                    >
                        Select term
                    </Form.Label>
                    <Form.Select
                        id="pth-term-selector"
                        value={selectedTerm?.code || ''}
                        onChange={(e) => onTermChange(e.target.value)}
                        className="pth-term-select"
                    >
                        {terms.map((t) => (
                            <option key={t.code} value={t.code}>
                                {termDisplayName(t)}
                                {t.isActive ? ' (Active)' : ''}
                            </option>
                        ))}
                    </Form.Select>
                </div>
            </div>

            {/* Term phase, the next deadline and the figures; every date is in
                Settings. A tab other than Overview keeps only the term and its
                one status, so its own work starts higher up. */}
            <Card
                className="border-0 shadow-sm mb-3"
                style={{ borderTop: `4px solid ${programColor}` }}
            >
                <Card.Body className="py-2">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        <span className="fw-semibold mb-0">
                            {termDisplayName(selectedTerm) ||
                                'No Term Selected'}
                        </span>
                        {!switchedOff && (
                            <Badge
                                bg={phaseConfig.bg}
                                text={badgeTextFor(phaseConfig.bg)}
                            >
                                {phaseConfig.label}
                            </Badge>
                        )}
                        {!compact &&
                            !switchedOff &&
                            selectedTerm?.applicationsOpen &&
                            isApplicationWindowOpen(
                                selectedTerm,
                                programType
                            ) && (
                                <Badge bg="success" className="ms-1">
                                    Accepting Applications
                                </Badge>
                            )}
                        {(!compact || switchedOff) && (
                            <span className="small text-muted ms-md-auto">
                                {switchedOff ? (
                                    `${programLabel} is not taking requests through the Hub this term`
                                ) : next ? (
                                    <>
                                        <strong>Next:</strong> {next.label} on{' '}
                                        {formatDate(next.date)} (
                                        {next.days === 0
                                            ? 'last day'
                                            : `${next.days} day${next.days !== 1 ? 's' : ''}`}
                                        )
                                    </>
                                ) : (
                                    'No upcoming deadlines'
                                )}
                            </span>
                        )}
                    </div>
                    {stats && !compact && (
                        <Row
                            as="ul"
                            className="list-unstyled g-1 mb-0 mt-2 pt-2 border-top"
                        >
                            {stats.map((stat, i) => (
                                <Col as="li" xs={6} md key={i}>
                                    <span
                                        className={`fs-5 fw-semibold me-1 text-${stat.color || 'primary'}`}
                                    >
                                        <StatValue
                                            value={stat.value}
                                            loading={statsLoading}
                                            failed={statsFailed}
                                        />
                                    </span>
                                    <small className="text-muted">
                                        {stat.label}
                                    </small>
                                </Col>
                            ))}
                        </Row>
                    )}
                </Card.Body>
            </Card>
        </>
    );
}

export default ProgramTermHeader;
